// Decides what the help center's refund / cancelation result pages show for
// the counts the refund-revenuecat-subscriptions and
// cancel-revenuecat-subscriptions edge functions report, so that every
// combination maps to a truthful message (e.g. a refund is only confirmed
// when one was made).

import type { RevenueCatSubscription } from "../types"

export const SUPPORT_EMAIL = "support@visualmind.app"

/** Which subscriptions the user asked to refund / cancel. */
type RequestType = "all" | "web"

interface StoreCounts {
  activeAppStoreSubscriptionsCount?: number
  activePlayStoreSubscriptionsCount?: number
  activeStripeSubscriptionsCount?: number
}

/**
 * - `success`: only good news (icon + "Thank you!").
 * - `warning`: the request didn't (fully) go through (icon + "We are sorry!").
 * - `furtherActionNeeded`: App Store / Play Store subscriptions the user still
 *   has to handle in the store, plus the web outcome (if any) above them.
 */
type ResultLayout = "furtherActionNeeded" | "success" | "warning"

type WebRefundOutcome =
  /** Canceled now and refunded. */
  | "refunded"
  /** Outside the refund window: canceled at period end, not refunded. */
  | "canceledNotRefunded"
  /** RevenueCat lists an active web subscription, but nothing was refunded or canceled. */
  | "notConfirmed"
  /** No active web subscription (and no refund completed by a retry). */
  | "noWebSubscription"

type WebCancelationOutcome =
  /** Canceled at period end. */
  | "canceled"
  /** RevenueCat lists an active web subscription, but nothing was canceled. */
  | "notConfirmed"
  /** No active web subscription. */
  | "noWebSubscription"

interface StoreInstructions {
  apple: boolean
  google: boolean
}

interface HelpCenterResult<Outcome> {
  /** Whether the user has an App Store or Play Store subscription. */
  hasStoreSubscription: boolean
  layout: ResultLayout
  /** Set when the user still has to refund / cancel in the stores. */
  storeInstructions: StoreInstructions | null
  web: Outcome
}

const positive = (count: number | undefined) => (count ?? 0) > 0

function decide<Outcome>(
  counts: StoreCounts,
  requestType: RequestType,
  web: Outcome,
  isSuccess: boolean,
): HelpCenterResult<Outcome> {
  const apple = positive(counts.activeAppStoreSubscriptionsCount)
  const google = positive(counts.activePlayStoreSubscriptionsCount)
  const hasStoreSubscription = apple || google

  const storeInstructions =
    hasStoreSubscription && requestType === "all" ? { apple, google } : null

  const layout: ResultLayout = storeInstructions
    ? "furtherActionNeeded"
    : isSuccess
      ? "success"
      : "warning"

  return { hasStoreSubscription, layout, storeInstructions, web }
}

export function decideRefundResult(
  counts: StoreCounts & {
    canceledStripeSubscriptionsCount?: number
    refundedStripeSubscriptionsCount?: number
  },
  refundType: RequestType,
): HelpCenterResult<WebRefundOutcome> {
  // NOTE: The edge function either refunds or cancels at period end, never
  // both; should both be reported, the refund is what the user asked for.
  const web: WebRefundOutcome = positive(
    counts.refundedStripeSubscriptionsCount,
  )
    ? "refunded"
    : positive(counts.canceledStripeSubscriptionsCount)
      ? "canceledNotRefunded"
      : positive(counts.activeStripeSubscriptionsCount)
        ? "notConfirmed"
        : "noWebSubscription"

  return decide(counts, refundType, web, web === "refunded")
}

export function decideCancelationResult(
  counts: StoreCounts & { canceledStripeSubscriptionsCount?: number },
  cancelationType: RequestType,
): HelpCenterResult<WebCancelationOutcome> {
  const web: WebCancelationOutcome = positive(
    counts.canceledStripeSubscriptionsCount,
  )
    ? "canceled"
    : positive(counts.activeStripeSubscriptionsCount)
      ? "notConfirmed"
      : "noWebSubscription"

  return decide(counts, cancelationType, web, web === "canceled")
}

/**
 * When the Pro access of the given (active) web subscriptions ends once
 * they're canceled at period end: the latest expiration date of the Stripe
 * ones, as an ISO string, or null when there's none.
 */
export function getWebAccessEndDate(
  subscriptions: Pick<RevenueCatSubscription, "expires_date" | "store">[],
): string | null {
  const expiresDates = subscriptions
    .filter(({ store }) => store === "stripe")
    .map(({ expires_date }) => expires_date)
    .filter((date) => !Number.isNaN(new Date(date).getTime()))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())

  return expiresDates[0] ?? null
}

/** The date in the given language (e.g. "21 October 2026"), or null when invalid. */
export function formatAccessEndDate(
  date: string | null | undefined,
  language: string | undefined,
): string | null {
  if (!date) return null

  const parsed = new Date(date)

  if (Number.isNaN(parsed.getTime())) return null

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  }

  try {
    return new Intl.DateTimeFormat(language, options).format(parsed)
  } catch {
    // NOTE: An invalid language tag (e.g. a malformed detected language).
    return new Intl.DateTimeFormat("en", options).format(parsed)
  }
}
